/**
 * The "a new version is available" bar.
 *
 * Renders nothing in the browser, nothing while checking, and nothing when the
 * install is current — so `App` can mount it unconditionally and forget it.
 *
 * It is a notice bar rather than a dialog on purpose. AEGIS is open beside a
 * trading platform during the session; a modal that steals focus, or an install
 * that restarts on its own schedule, would take the chart away at the exact
 * moment the user needs it. The check happens on launch, the bar waits, and the
 * restart is something someone chose.
 *
 * Dismissing hides it for this run only. It is not a decision worth persisting:
 * the next launch checks again, and an update someone waved away last week is
 * still one they are missing.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { isDesktop } from '../lib/runtime.js';
import { getPrefs } from '../lib/desktop.js';
import { checkForUpdate, installUpdate, type UpdateState } from '../lib/updates.js';

export function UpdateNotice(): JSX.Element | null {
  const [state, setState] = useState<UpdateState>({ kind: 'idle' });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isDesktop()) return;
    let cancelled = false;

    void (async () => {
      // The preference gates the automatic check only. The button in Settings
      // ignores it, because pressing it *is* the request.
      const prefs = await getPrefs();
      if (cancelled || !prefs?.checkUpdatesOnLaunch) return;

      const result = await checkForUpdate();
      // A failed launch check stays quiet. Nobody asked, and a bar reading
      // "could not reach the update server" on every offline launch is noise.
      // Settings still reports the failure to anyone who goes looking.
      if (!cancelled && result.kind === 'available') setState(result);
    })();

    return () => { cancelled = true; };
  }, []);

  if (dismissed) return null;
  if (state.kind !== 'available' && state.kind !== 'installing' && state.kind !== 'failed') return null;

  const install = async (): Promise<void> => {
    setState({ kind: 'installing' });
    setState(await installUpdate());
  };

  if (state.kind === 'installing') {
    return (
      <div className="notice-bar" role="status">
        Downloading AEGIS. The app will restart when it is ready.
      </div>
    );
  }

  if (state.kind === 'failed') {
    return (
      <div className="notice-bar" role="alert">
        {state.reason}
        <button className="compact error-dismiss" onClick={() => setDismissed(true)}>dismiss</button>
      </div>
    );
  }

  return (
    <div className="notice-bar" role="status">
      AEGIS {state.status.version} is available — you are on {state.status.currentVersion}.
      {' '}
      <button className="btn btn-primary compact" onClick={() => void install()}>
        Install and restart
      </button>
      <button className="compact error-dismiss" onClick={() => setDismissed(true)}>not now</button>
    </div>
  );
}
